import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Drawer,
  Divider,
  Chip,
  Autocomplete,
} from '@mui/material';
import {
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useCompanyCore } from '../../../../core/modules/company';
import { useMasterCore } from '../../../../core/modules/master';
import { ContainerCategory } from '../../../../core/interfaces/category.interface';
import { useLocationCore } from '../../../../core/modules/location';
import { useWarehouseCore } from '../../../../core/modules/warehouse';
import { GetContainersParams } from '../../../../core/modules/container/interfaces/get-containers';
import { DESTINY_CONTAINER_TYPES } from '../../../../core/constants/containerTypes';
import ContainerCategorySelect from '../../shared/ContainerCategorySelect';

interface LocateContainerFiltersProps {
  filters: GetContainersParams;
  onFiltersChange: (filters: GetContainersParams) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  loading?: boolean;
}

export const LocateContainerFilters: React.FC<LocateContainerFiltersProps> = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
  loading = false,
}) => {
  const { companyId } = useCompanyCore();
  const {
    priceRanges,
    containerTypes,
    containerCategories,
    containerRecipientTypes,
    getTypes,
    getCategories,
  } = useMasterCore();
  const { locations, getLocations } = useLocationCore();
  const { warehouses, getWarehouses } = useWarehouseCore();

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<GetContainersParams>(filters);
  const [locationInputValue, setLocationInputValue] = useState('');

  // Cargar datos necesarios
  useEffect(() => {
    if (companyId) {
      getLocations(companyId);
      getTypes(companyId, 'containerRecipient');
      getTypes(companyId, 'container');
      getCategories(companyId, 'container');
      getWarehouses(companyId);
    }
  }, [companyId, getLocations, getTypes, getCategories, getWarehouses]);

  // Sincronizar el estado local con los filtros externos
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (field: keyof GetContainersParams, value: any) => {
    setLocalFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      onFiltersChange(newFilters);
      return newFilters;
    });
  };

  const handleApplyFilters = () => {
    onApplyFilters();
  };

  const handleClearFilters = () => {
    const clearedFilters: GetContainersParams = {
      page: 1,
    };
    setLocalFilters(clearedFilters);
    setLocationInputValue('');
    onFiltersChange(clearedFilters);
    onClearFilters();
  };

  const hasActiveFilters = Object.keys(filters).some(
    key => key !== 'page' && filters[key as keyof GetContainersParams] !== undefined
  );

  const activeFiltersCount = Object.keys(filters).filter(
    key => key !== 'page' && filters[key as keyof GetContainersParams] !== undefined
  ).length;

  // Ya no se usa containerTypeOptions ni containerCategoryOptions, se usa ContainerCategorySelect y containerRecipientTypes

  const warehouseMap = useMemo(() => {
    const map = new Map<number, string>();
    warehouses?.forEach((warehouse) => {
      map.set(warehouse.id, warehouse.code);
    });
    return map;
  }, [warehouses]);

  const locationOptions = useMemo(() => {
    const baseOptions = (locations || []).map((location) => ({
      ...location,
      label: `${location.code} - ${warehouseMap.get(location.warehouse_id) || 'SIN BODEGA'}`,
    }));

    return [
      {
        id: -1,
        code: 'SIN_UBICACION',
        description: 'Contenedores sin ubicación',
        warehouse_id: 0,
        label: 'Sin ubicación',
      },
      ...baseOptions,
    ];
  }, [locations, warehouseMap]);

  const filteredLocationOptions = useMemo(() => {
    if (!localFilters.warehouse_id) return locationOptions;
    return locationOptions.filter((option) => option.id === -1 || option.warehouse_id === localFilters.warehouse_id);
  }, [locationOptions, localFilters.warehouse_id]);

  const selectedWarehouse = useMemo(() => {
    if (!localFilters.warehouse_id) return null;
    return warehouses?.find((warehouse) => warehouse.id === localFilters.warehouse_id) || null;
  }, [localFilters.warehouse_id, warehouses]);

  return (
    <Box>
      {/* Botón de filtros a la derecha */}
      <Button
        variant="outlined"
        startIcon={<FilterIcon />}
        onClick={() => setFiltersOpen(true)}
        disabled={!companyId}
        sx={{
          minWidth: 120,
          height: 56, // Match the TextField height
          borderRadius: 2,
          borderColor: hasActiveFilters ? 'primary.main' : 'grey.300',
          color: hasActiveFilters ? 'primary.main' : 'text.primary',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'primary.50',
          }
        }}
      >
        Filtros
        {hasActiveFilters && (
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              ml: 1
            }}
          />
        )}
      </Button>

      {/* Drawer de filtros */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 400 },
            p: 3,
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold">
              Filtros
            </Typography>
            {hasActiveFilters && (
              <Chip
                label={`${activeFiltersCount} activo${activeFiltersCount !== 1 ? 's' : ''}`}
                color="primary"
                size="small"
              />
            )}
          </Box>
          <IconButton onClick={() => setFiltersOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>

          {/* Código de contenedor */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Código de contenedor"
              value={localFilters.code || ''}
              onChange={(e) => handleFilterChange('code', e.target.value || undefined)}
              placeholder="Buscar por código"
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={localFilters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value || undefined)}
                label="Estado"
              >
                <MenuItem value="">Todos</MenuItem>
                {DESTINY_CONTAINER_TYPES.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Categoría */}
          <Grid item xs={12}>
            <ContainerCategorySelect
              value={localFilters.category || undefined}
              onChange={(newValue: number | undefined | string) => {
                if (typeof newValue === 'number') {
                  handleFilterChange('category', newValue);
                } else {
                  handleFilterChange('category', undefined);
                }
              }}
              categories={containerCategories as ContainerCategory[] || []}
              label="Categoría"
              showAllOption={true}
              size="small"
            />
          </Grid>

          {/* Tipo de recipiente */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de recipiente</InputLabel>
              <Select
                value={localFilters.recipientType || ''}
                onChange={(e) => handleFilterChange('recipientType', e.target.value || undefined)}
                label="Tipo de recipiente"
              >
                <MenuItem value="">Todos</MenuItem>
                {containerRecipientTypes?.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Ubicación */}
          <Grid item xs={12}>
            <Autocomplete
              options={filteredLocationOptions}
              getOptionLabel={(option) => option.label}
              value={filteredLocationOptions.find((option) => option.id === localFilters.location_id) || null}
              inputValue={locationInputValue}
              onInputChange={(_, newInputValue) => {
                setLocationInputValue(newInputValue);
              }}
              onChange={(_, value) => {
                handleFilterChange('location_id', value?.id ?? undefined);
                if (!value) {
                  setLocationInputValue('');
                }
              }}
              open={locationInputValue.length > 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Ubicación"
                  placeholder="Buscar ubicación"
                  size="small"
                />
              )}
              noOptionsText="No hay ubicaciones disponibles"
              clearText="Limpiar"
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </Grid>

          {/* Bodega */}
          <Grid item xs={12}>
            <Autocomplete
              options={warehouses || []}
              getOptionLabel={(option) => option.code}
              value={selectedWarehouse}
              onChange={(_, value) => {
                setLocalFilters(prev => {
                  const newFilters = {
                    ...prev,
                    warehouse_id: value?.id || undefined,
                    location_id: value ? prev.location_id : undefined,
                  };
                  if (!value) {
                    newFilters.location_id = undefined;
                  } else if (prev.location_id) {
                    const selectedLocation = locationOptions.find((option) => option.id === prev.location_id);
                    if (selectedLocation && selectedLocation.warehouse_id !== value.id) {
                      newFilters.location_id = undefined;
                    }
                  }
                  onFiltersChange(newFilters);
                  return newFilters;
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bodega"
                  placeholder="Seleccionar bodega"
                  size="small"
                />
              )}
              clearText="Limpiar"
              isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
          </Grid>


          {/* Rango de precios */}
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Rango de precios</InputLabel>
              <Select
                value={localFilters.priceRange || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'sin-rango') {
                    handleFilterChange('priceRange', 'sin-rango');
                  } else {
                    handleFilterChange('priceRange', value || undefined);
                  }
                }}
                label="Rango de precios"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="sin-rango">Sin rango</MenuItem>
                {priceRanges?.map((priceRange) => (
                  <MenuItem key={priceRange.id} value={priceRange.id}>
                    {priceRange.description}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Botones de acción */}
        <Box sx={{ display: 'flex', gap: 2, mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            variant="outlined"
            onClick={handleClearFilters}
            fullWidth
            startIcon={<ClearIcon />}
            disabled={!hasActiveFilters}
            sx={{ borderRadius: 2 }}
          >
            Limpiar Filtros
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              handleApplyFilters();
              setFiltersOpen(false);
            }}
            fullWidth
            disabled={loading || !companyId}
            sx={{ borderRadius: 2 }}
          >
            Aplicar Filtros
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};
